import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import type {
  StandardCart, StandardOrder, StandardOrderDiscount,
} from '@/types/ShopFront/CheckoutStandards'
import type { ConsignmentInput } from './augmentConsignments'
import type { StandardAddressStoreforntInput } from './standardizeAddressStorefrontCheckoutApi'
import { quoteShippingRateNoDiscount } from '../FramedCheckout/shipmentServices/quoteShippingRate'
import { checkIfFreeShippingEnabled } from '../../helpers/checkIfFreeShippingEnabled'
import { nonNull } from '../../helpers/nullish'

type OrderStandardizerPropInput = {
  consignments: readonly ConsignmentInput[]
  coupons: readonly StandardOrderDiscount[]
  totalDiscount?: number
  billingAddress: StandardAddressStoreforntInput
  grandTotal: number
  taxTotal: number,
  subtotal: number,
  shippingCostBeforeDiscount: number
  shippingCostTotal: number
  orderId: null | number | string
  status?: string | null
  paymentMethod?: {
    mask: string,
    method: string,
  };
  customerId?: number
  isStoreCreditApplied?: boolean
  outstandingBalance?: number
  completedAt?: string
  shippedShieldLineItem?: {
    name: string,
    imageUrl: string,
    listPrice: number,
  }
}

export type OrderStandardizer = (
  (props: OrderStandardizerPropInput, cart: StandardCart) => Promise<StandardOrder>
)

export type OrderStandardizerPromise = (
  (props: OrderStandardizerPropInput, cart: StandardCart) => Promise<StandardOrder>
)

export const getOrderStandardizer = ({
  augmentConsignments,
  standardizeAddressStorefrontCheckoutApi,
  calculateTotalDiscountFromLineItems,
}: {
  augmentConsignments: Awaited<typeof import('@/services/Standardizers/augmentConsignments')>['augmentConsignments']
  standardizeAddressStorefrontCheckoutApi: Awaited<typeof import('@/services/Standardizers/standardizeAddressStorefrontCheckoutApi')>['standardizeAddressStorefrontCheckoutApi']
  calculateTotalDiscountFromLineItems: Awaited<typeof import('@/helpers/checkoutHelpers/calculateTotalDiscountFromLineItems')>['calculateTotalDiscountFromLineItems']
}): OrderStandardizer => async ({
  consignments,
  billingAddress,
  coupons,
  grandTotal,
  taxTotal,
  shippingCostBeforeDiscount,
  shippingCostTotal,
  orderId,
  status,
  subtotal,
  paymentMethod,
  customerId = 0,
  isStoreCreditApplied = false,
  outstandingBalance = 0,
  totalDiscount,
  completedAt = '',
  shippedShieldLineItem,
}, cart) => {
  const shipments = augmentConsignments({ consignments, cart })
  const discountFromCoupons = (coupons?.[0]?.discountedAmount || 0)
  const storeCreditsOverflow = 0
  const storeCreditDiscount = 0
  let customShippingRateNoDiscount: number | undefined
  if (await checkIfFreeShippingEnabled()) {
    customShippingRateNoDiscount = await quoteShippingRateNoDiscount({
      lineItems: cart?.lineItems,
      address: consignments[0]?.shippingAddress,
      selectedShippingOptionDescriptions: consignments.map(
        (consignment) => consignment.selectedShippingOption?.description,
      ).filter(nonNull),
    })
    if (customShippingRateNoDiscount
      && customShippingRateNoDiscount === shippingCostBeforeDiscount) {
      customShippingRateNoDiscount = undefined
    }
  }
  return {
    totalDiscount: totalDiscount || calculateTotalDiscountFromLineItems(cart?.lineItems),
    shipments,
    pickupOnly: shipments.every((consignment) => consignment.isBopus),
    billingAddress: standardizeAddressStorefrontCheckoutApi(billingAddress),
    coupons: [...coupons].map((i) => ({ ...i })),
    grandTotal,
    storeCreditDiscount,
    storeCreditsOverflow,
    subtotal,
    taxTotal,
    cart,
    shippingCostBeforeDiscount,
    shippingCostTotal,
    orderId,
    status: status || 'INCOMPLETE',
    paymentMethod,
    customerId,
    email: cart.email,
    isStoreCreditApplied: !!isStoreCreditApplied,
    discountFromCoupons,
    outstandingBalance,
    completedAt,
    shippedShieldLineItem,
    customShippingRateNoDiscount,
  }
}

export const standardizeOrder: OrderStandardizerPromise = async (props, cart) => {
  const [
    { augmentConsignments },
    { standardizeAddressStorefrontCheckoutApi },
    { calculateTotalDiscountFromLineItems },
  ] = await allPromisesWithRetries(() => [
    import('@/services/Standardizers/augmentConsignments'),
    import('@/services/Standardizers/standardizeAddressStorefrontCheckoutApi'),
    import('@/helpers/checkoutHelpers/calculateTotalDiscountFromLineItems'),
  ])

  const standardizer = getOrderStandardizer({
    augmentConsignments,
    standardizeAddressStorefrontCheckoutApi,
    calculateTotalDiscountFromLineItems,
  })

  return standardizer(props, cart)
}

export default standardizeOrder
